import { matchPath } from 'react-router-dom';
import pluralize from 'pluralize';

import { FacilityPayload } from '@/hooks/useFacilitiesQuery';
import { RoutePath } from '@/routes/RoutePath';
import { FacilityFacilityTypeEnum } from '@/types/crm-screener';

interface FacilityTypeDisplayConfig {
  groupLabel: string;
  unit: string;
}

const facilityTypeDisplayConfigMap: Partial<
  Record<FacilityFacilityTypeEnum, FacilityTypeDisplayConfig>
> = {
  [FacilityFacilityTypeEnum.SkilledNursing]: {
    groupLabel: 'All Skilled Nursing Facilities',
    unit: 'Facility'
  },
  [FacilityFacilityTypeEnum.HomeHealth]: {
    groupLabel: 'All Home Health Agencies',
    unit: 'Agency'
  },
  [FacilityFacilityTypeEnum.CriticalCare]: {
    groupLabel: 'All Critical Care Facilities',
    unit: 'Facility'
  },
  [FacilityFacilityTypeEnum.AssistedLiving]: {
    groupLabel: 'All Assisted Living Facilities',
    unit: 'Facility'
  },
  [FacilityFacilityTypeEnum.LongTermCareHospital]: {
    groupLabel: 'All Long-Term Care Hospitals',
    unit: 'Hospital'
  },
  [FacilityFacilityTypeEnum.Hospice]: {
    groupLabel: 'All Hospice Agencies',
    unit: 'Agency'
  }
};

const defaultFacilityTypeDisplayConfig: FacilityTypeDisplayConfig = {
  groupLabel: 'All Facilities',
  unit: 'Facility'
};

const getFacilityTypeDisplayConfig = (
  facilityType: FacilityFacilityTypeEnum
): FacilityTypeDisplayConfig =>
  facilityTypeDisplayConfigMap[facilityType] ??
  defaultFacilityTypeDisplayConfig;

export const formatFacilityTypeCountLabel = (
  facilityType: FacilityFacilityTypeEnum,
  count: number
) => {
  const { unit } = getFacilityTypeDisplayConfig(facilityType);
  return `${count} ${pluralize(unit, count)}`;
};

export const getGenericAllLabelForType = (
  facilityType: FacilityFacilityTypeEnum
) => {
  const { unit } = getFacilityTypeDisplayConfig(facilityType);
  return `All ${pluralize(unit)}`;
};

const RESIDENTS_ROUTE_ALLOWED_FACILITY_TYPES: FacilityFacilityTypeEnum[] = [
  FacilityFacilityTypeEnum.SkilledNursing,
  FacilityFacilityTypeEnum.LongTermCareHospital
];

export const isAutoDecisioningAdminHubPath = (pathname: string): boolean =>
  Boolean(
    matchPath(
      {
        path: RoutePath.AdminHubAutoDecisioning,
        end: false,
        caseSensitive: true
      },
      pathname
    )
  );

export const getMinimumFacilityCountForAllView = (pathname?: string): number =>
  pathname && isAutoDecisioningAdminHubPath(pathname) ? 1 : 2;

export const getAllowedFacilityTypesForRoute = (
  pathname: string
): FacilityFacilityTypeEnum[] | undefined =>
  pathname === RoutePath.CrmScreenerResidentsAllResidents
    ? RESIDENTS_ROUTE_ALLOWED_FACILITY_TYPES
    : undefined;

export const isFacilityTypeAllowed = (
  facilityType: FacilityFacilityTypeEnum | undefined,
  allowedFacilityTypes?: FacilityFacilityTypeEnum[]
) =>
  !allowedFacilityTypes ||
  (facilityType !== undefined && allowedFacilityTypes.includes(facilityType));

export const getFacilityIdsByFacilityType = (
  facilities: FacilityPayload[],
  accessibleFacilityIds: ReadonlySet<string>
): Partial<Record<FacilityFacilityTypeEnum, string[]>> =>
  facilities.reduce<Partial<Record<FacilityFacilityTypeEnum, string[]>>>(
    (acc, facility) => {
      if (!facility.facility_type || !accessibleFacilityIds.has(facility.id)) {
        return acc;
      }
      acc[facility.facility_type] = [
        ...(acc[facility.facility_type] ?? []),
        facility.id
      ];
      return acc;
    },
    {}
  );

export const getFacilityIdsForFacilityType = (
  facilities: FacilityPayload[],
  accessibleFacilityIds: ReadonlySet<string>,
  facilityType: FacilityFacilityTypeEnum
): string[] =>
  facilities
    .filter(
      (facility) =>
        facility.facility_type === facilityType &&
        accessibleFacilityIds.has(facility.id)
    )
    .map((facility) => facility.id);

export interface FacilityTypeGroupOption {
  facilityIds: string[];
  facilityType: FacilityFacilityTypeEnum;
  label: string;
  subtitle: string;
}

export const getFacilityTypeGroupOptions = (
  facilities: FacilityPayload[],
  accessibleFacilityIds: ReadonlySet<string>
): FacilityTypeGroupOption[] => {
  const facilityIdsByType = getFacilityIdsByFacilityType(
    facilities,
    accessibleFacilityIds
  );

  return Object.entries(facilityIdsByType)
    .map(([facilityType, facilityIds]) => {
      const normalizedFacilityType = facilityType as FacilityFacilityTypeEnum;
      const displayConfig = getFacilityTypeDisplayConfig(
        normalizedFacilityType
      );
      return {
        facilityIds: facilityIds ?? [],
        facilityType: normalizedFacilityType,
        label: displayConfig.groupLabel,
        subtitle: formatFacilityTypeCountLabel(
          normalizedFacilityType,
          facilityIds?.length ?? 0
        )
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};
